'use client';

import { useAuth0 } from '@auth0/auth0-react';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

import {
    AUTH0_LOYALTY_ID_CLAIM,
    type Auth0IdTokenClaims,
    CombinedUserClaims,
    decodeJwtCookie,
    getCustomerUuid,
    type IdentityUser,
    isAuth0AosSession,
    mapAuth0IdTokenToIdentityUser,
} from '@libs/common-next';
import {
    AUTH_COOKIES,
    getAccessToken,
    isAccessTokenAuth0,
    isLastActiveExpired,
    SESSION_EVALUATED_DOM_EVENT,
    subscribeToAuth0ClaimsRefresh,
} from '@libs/core';

/** Returns the mapped customer uuid only when the claims yield a usable identity. */
function getCustomerUuidFromClaim(mappedUser: CombinedUserClaims | undefined) {
    const customerUuid = mappedUser?.['petsmart.com/uuid'];
    return typeof customerUuid === 'string' && customerUuid.length > 0 ? customerUuid : undefined;
}

/**
 * Maps the Auth0 access-token cookie into the session user for Auth0 sessions that
 * exist only as cookies, with no SDK session to read an `id_token` from.
 *
 * AOS impersonation exchanges the agent's Okta token for a customer access token
 * server-side, writes it with `setAuth0AccessToken`, then opens the storefront in a
 * new tab without a corresponding customer SDK session. Safari can also finish SDK
 * init reporting `isAuthenticated: false` while those cookies are still valid.
 * This mirrors the cookie-preserving branch in `Auth0TokenSync`, so identity and
 * cookies survive the same conditions. Returns nothing once logout clears the
 * cookies, or when the access token is not an Auth0 token carrying identity claims.
 */
function readAuth0AccessTokenCookieClaims() {
    if (!isAccessTokenAuth0() || !getAccessToken()) {
        return undefined;
    }

    return decodeJwtCookie<Auth0IdTokenClaims>(AUTH_COOKIES.ACCESS_TOKEN);
}

/**
 * Maps a Janrain access-token cookie issued before `auth0` was enabled. Without it the
 * shopper still passes cookie-gated routes but never gets a session `user`, so the
 * profile never loads. The shopper moves to Auth0 at their next sign-in.
 */
function readLegacyAccessTokenUser() {
    if (isAccessTokenAuth0() || !getAccessToken()) {
        return undefined;
    }

    return decodeJwtCookie<IdentityUser>(AUTH_COOKIES.ACCESS_TOKEN);
}

function isAccessTokenExpired(claims: Auth0IdTokenClaims): boolean {
    return typeof claims.exp === 'number' && Date.now() >= claims.exp * 1000;
}

function mapActiveAuth0AccessTokenCookieUser(claims: Auth0IdTokenClaims | undefined) {
    if (!claims || isAccessTokenExpired(claims)) {
        return undefined;
    }

    return mapAuth0IdTokenToIdentityUser(claims);
}

function syncMappedUser(
    mappedUser: CombinedUserClaims | undefined,
    setUser: Dispatch<SetStateAction<IdentityUser | CombinedUserClaims>>,
    replaceOnLoyaltyIdChange = false
) {
    const customerUuid = getCustomerUuidFromClaim(mappedUser);
    if (!mappedUser || !customerUuid) {
        setUser(undefined);
        return;
    }

    setUser(prev => {
        // The profile merge can already have written the enrolled id into `loyaltyid`,
        // so only the namespaced claim reveals that a pending token was re-minted.
        const isSameLoyaltyId =
            prev?.loyaltyid === mappedUser.loyaltyid &&
            (prev as CombinedUserClaims | undefined)?.[AUTH0_LOYALTY_ID_CLAIM] === mappedUser[AUTH0_LOYALTY_ID_CLAIM];
        const isSameUser = getCustomerUuid(prev) === customerUuid && (!replaceOnLoyaltyIdChange || isSameLoyaltyId);
        return isSameUser ? prev : mappedUser;
    });
}

function clearUserAtTokenExpiry(exp: number | undefined, clearUser: () => void): (() => void) | undefined {
    if (typeof exp !== 'number') {
        return undefined;
    }

    const timeoutId = globalThis.setTimeout(clearUser, Math.max(0, exp * 1000 - Date.now()));
    return () => globalThis.clearTimeout(timeoutId);
}

/**
 * When `auth0` is on, maps the Auth0 `id_token` into the shared session `user`
 * and clears it on logout. When off, it's a no-op so the legacy cookie-JWT flow
 * stays the sole owner of `user`.
 *
 * Uses `getIdTokenClaims()` rather than `useAuth0().user` because the SDK strips
 * the registered claims (`sid`, `exp`, `aud`, `iss`) the {@link IdentityUser}
 * contract needs. Falls back to the access-token cookie when the SDK holds no
 * session — see {@link readAuth0AccessTokenCookieClaims}.
 */
export function useSyncAuth0IdTokenUser(
    isEnabled: boolean,
    setUser: Dispatch<SetStateAction<IdentityUser | CombinedUserClaims>>
) {
    const { getIdTokenClaims, isAuthenticated, isLoading } = useAuth0();
    const [isIdTokenUserSynced, setIsIdTokenUserSynced] = useState(!isEnabled);
    const [claimsVersion, setClaimsVersion] = useState(0);

    // Claims written to `app_metadata` after login only reach the app once the token is
    // re-minted, which can happen in this tab or in another one sharing the SDK cache.
    // Bumping the counter re-runs the mapping below against the newly cached claims.
    useEffect(() => {
        if (!isEnabled) {
            return;
        }

        return subscribeToAuth0ClaimsRefresh(() => {
            setClaimsVersion(version => version + 1);
        });
    }, [isEnabled]);

    // A Janrain refresh or logout rewrites the access-token cookie without changing any
    // Auth0 SDK state. Auth0 tokens are skipped so they don't refetch id-token claims on
    // every session evaluation.
    useEffect(() => {
        if (!isEnabled) {
            return;
        }

        const onSessionEvaluated = () => {
            if (!isAccessTokenAuth0()) {
                setClaimsVersion(version => version + 1);
            }
        };
        globalThis.window.addEventListener(SESSION_EVALUATED_DOM_EVENT, onSessionEvaluated);
        return () => globalThis.window.removeEventListener(SESSION_EVALUATED_DOM_EVENT, onSessionEvaluated);
    }, [isEnabled]);

    useEffect(() => {
        // Flag off: legacy cookie-JWT flow owns `user`. Never touch it here — this
        // effect can re-run (e.g. a new `getIdTokenClaims`/auth-state reference)
        // while the wrapper's cookie-repopulation effect (keyed on the flag) does
        // not, so clearing here would log a legacy shopper out until remount.
        if (!isEnabled) {
            setIsIdTokenUserSynced(true);
            return;
        }

        // Auth0 is still resolving: keep the current value and wait for the next run.
        if (isLoading) {
            setIsIdTokenUserSynced(false);
            return;
        }

        const clearUser = () => setUser(undefined);

        // An expired inactivity window ends both SDK-backed and cookie-only sessions.
        if (isLastActiveExpired()) {
            clearUser();
            setIsIdTokenUserSynced(true);
            return;
        }

        const cookieClaims = readAuth0AccessTokenCookieClaims();
        const cookieUser = mapActiveAuth0AccessTokenCookieUser(cookieClaims);

        // AOS has no SDK id_token. Its exchanged customer token must remain the
        // identity source even when another shopper is still authenticated in
        // the Auth0 SDK on this browser.
        if (isAuth0AosSession(cookieClaims)) {
            syncMappedUser(cookieUser, setUser);
            setIsIdTokenUserSynced(true);
            return cookieUser ? clearUserAtTokenExpiry(cookieClaims?.exp, clearUser) : undefined;
        }

        // The SDK holds no session. A cookie-only Auth0 session (AOS impersonation,
        // Safari) or a pre-Auth0 Janrain session still carries a usable identity, so
        // map that before giving up. Otherwise the visitor is not (or no longer)
        // authenticated: drop any previously mapped user so a logout can't leave a
        // stale session behind.
        if (!isAuthenticated) {
            const legacyUser = readLegacyAccessTokenUser();
            if (legacyUser) {
                setUser(prev => (prev?.sid === legacyUser.sid ? prev : legacyUser));
                setIsIdTokenUserSynced(true);
                return;
            }

            syncMappedUser(cookieUser, setUser);
            setIsIdTokenUserSynced(true);
            return cookieUser ? clearUserAtTokenExpiry(cookieClaims?.exp, clearUser) : undefined;
        }

        let isMounted = true;
        setIsIdTokenUserSynced(false);

        async function syncUserFromIdToken() {
            try {
                try {
                    const idTokenClaims = (await getIdTokenClaims()) as Auth0IdTokenClaims | undefined;

                    if (!isMounted) {
                        return;
                    }

                    // Auth0 still reports the visitor as authenticated, but the id token
                    // yielded no usable identity. Clear any previously mapped user so a
                    // stale session can't linger with outdated identity data.
                    const mappedUser = idTokenClaims ? mapAuth0IdTokenToIdentityUser(idTokenClaims) : undefined;
                    syncMappedUser(mappedUser, setUser, true);
                } catch {
                    // The claims fetch rejected: treat it as no usable identity and
                    // clear any previously mapped user so a failed refresh can't keep
                    // serving a stale session after Auth0 auth state has moved on.
                    if (isMounted) {
                        clearUser();
                    }
                    return;
                }
            } finally {
                if (isMounted) {
                    setIsIdTokenUserSynced(true);
                }
            }
        }

        syncUserFromIdToken();

        return () => {
            isMounted = false;
        };
    }, [claimsVersion, getIdTokenClaims, isAuthenticated, isEnabled, isLoading, setUser]);

    return isIdTokenUserSynced;
}
